// Calendar file for reminders (SPEC.md §8). Times are "floating" local times (no time zone), so a
// 07:30 session stays at 07:30 on whatever time zone the phone is in. Every event has a stable UID,
// and SEQUENCE grows with each export, so calendars that honour UIDs update events in place.
import { addDays, format, parse } from 'date-fns';
import { parseDays, WEEKDAYS, type ScheduleException, type TreatmentSchedule, type Weekday } from './schedule';
import { medTimes, type MedicationRow } from './meds';

const BYDAY: Record<Weekday, string> = { mon: 'MO', tue: 'TU', wed: 'WE', thu: 'TH', fri: 'FR', sat: 'SA', sun: 'SU' };
const DOMAIN = 'getrenalflow.com';

export interface ReminderOptions {
  sessions: boolean;
  medications: boolean;
  vaccinations: boolean;
  /** Minutes before a session for its alarm. */
  sessionAlarmMinutes: number;
}

export interface ReminderInput {
  schedule: TreatmentSchedule | null;
  exceptions: (ScheduleException & { id: string; note: string | null })[];
  /** Every medication, including stopped ones, so their events can be cancelled. */
  medications: (MedicationRow & { deleted_at?: string | null })[];
  vaccinations: { id: string; vaccine_name: string; dose_number: string | null; next_due_date: string | null }[];
  /** Local date the file is made on, 'yyyy-MM-dd'. */
  today: string;
}

function escapeText(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

/** Lines longer than 75 octets continue on the next line after a space (RFC 5545 §3.1). */
function fold(line: string) {
  const bytes = new TextEncoder().encode(line);
  if (bytes.length <= 75) return line;
  const parts: string[] = [];
  let current = '';
  let size = 0;
  for (const char of line) {
    const length = new TextEncoder().encode(char).length;
    if (size + length > (parts.length ? 74 : 75)) {
      parts.push(current);
      current = '';
      size = 0;
    }
    current += char;
    size += length;
  }
  parts.push(current);
  return parts.join('\r\n ');
}

const localDateTime = (date: string, time: string) => `${date.replace(/-/g, '')}T${time.replace(':', '')}00`;
const dateValue = (date: string) => date.replace(/-/g, '');
const parseDate = (date: string) => parse(date, 'yyyy-MM-dd', new Date());
const weekdayOf = (date: string): Weekday => WEEKDAYS[(parseDate(date).getDay() + 6) % 7];

/** The first date on or after `from` that falls on one of `days`. */
function firstOn(from: string, days: Weekday[]) {
  for (let offset = 0; offset < 7; offset++) {
    const date = format(addDays(parseDate(from), offset), 'yyyy-MM-dd');
    if (days.includes(weekdayOf(date))) return date;
  }
  return from;
}

interface Event {
  uid: string;
  summary: string;
  description?: string;
  location?: string | null;
  /** Timed: start date + 'HH:mm'. All-day: date only. */
  date: string;
  time: string | null;
  durationMin?: number | null;
  rrule?: string;
  exdates?: string[];
  alarm?: { trigger: string; text: string };
  cancelled?: boolean;
}

function eventLines(event: Event, stamp: string, sequence: number): string[] {
  const lines = ['BEGIN:VEVENT', `UID:${event.uid}`, `DTSTAMP:${stamp}`, `SEQUENCE:${sequence}`, `SUMMARY:${escapeText(event.summary)}`];
  if (event.time) {
    lines.push(`DTSTART:${localDateTime(event.date, event.time)}`);
    lines.push(`DURATION:PT${event.durationMin ?? 15}M`);
  } else {
    lines.push(`DTSTART;VALUE=DATE:${dateValue(event.date)}`);
    lines.push(`DTEND;VALUE=DATE:${dateValue(format(addDays(parseDate(event.date), 1), 'yyyy-MM-dd'))}`);
  }
  if (event.rrule) lines.push(`RRULE:${event.rrule}`);
  for (const exdate of event.exdates ?? []) lines.push(event.time ? `EXDATE:${localDateTime(exdate, event.time)}` : `EXDATE;VALUE=DATE:${dateValue(exdate)}`);
  if (event.location) lines.push(`LOCATION:${escapeText(event.location)}`);
  if (event.description) lines.push(`DESCRIPTION:${escapeText(event.description)}`);
  if (event.cancelled) lines.push('STATUS:CANCELLED');
  else lines.push('STATUS:CONFIRMED', 'TRANSP:TRANSPARENT');
  if (event.alarm && !event.cancelled) lines.push('BEGIN:VALARM', 'ACTION:DISPLAY', `DESCRIPTION:${escapeText(event.alarm.text)}`, `TRIGGER:${event.alarm.trigger}`, 'END:VALARM');
  lines.push('END:VEVENT');
  return lines;
}

function sessionEvents(input: ReminderInput, options: ReminderOptions): Event[] {
  const { schedule, exceptions, today } = input;
  const days = parseDays(schedule?.days_of_week);
  if (!schedule || !days.length) return [];
  const from = schedule.active_from > today ? schedule.active_from : today;
  const upcoming = exceptions.filter((exception) => exception.date >= today);
  const alarm = schedule.start_time
    ? { trigger: `-PT${options.sessionAlarmMinutes}M`, text: 'Dialysis session' }
    : { trigger: 'PT8H', text: 'Dialysis today' };
  const summary = 'Dialysis';
  const base = { summary, location: schedule.location, durationMin: schedule.duration_min ?? 240, alarm };
  const events: Event[] = [
    {
      ...base,
      uid: `${schedule.id}-0@${DOMAIN}`,
      date: firstOn(from, days),
      time: schedule.start_time,
      rrule: `FREQ=WEEKLY;BYDAY=${days.map((day) => BYDAY[day]).join(',')}${schedule.active_to ? `;UNTIL=${dateValue(schedule.active_to)}` : ''}`,
      // Skipped and moved sessions are taken out of the series; moved ones come back as their own event.
      exdates: upcoming.filter((exception) => exception.kind !== 'extra' && days.includes(weekdayOf(exception.date))).map((exception) => exception.date)
    }
  ];
  for (const exception of upcoming) {
    if (exception.kind === 'skip') continue;
    events.push({
      ...base,
      uid: `${exception.id}-0@${DOMAIN}`,
      date: exception.date,
      time: exception.new_time ?? schedule.start_time,
      description: exception.note ?? (exception.kind === 'extra' ? 'Extra session' : 'Moved session')
    });
  }
  return events;
}

function medicationEvents(input: ReminderInput): Event[] {
  const scheduleDays = parseDays(input.schedule?.days_of_week);
  const events: Event[] = [];
  for (const med of input.medications) {
    const times = medTimes(med);
    const stopped = !!med.deleted_at || !med.active || !!med.given_at_center || (med.end_date != null && med.end_date < input.today);
    let days: Weekday[] | null = null;
    if (med.day_rule === 'dialysis_days') days = scheduleDays;
    else if (med.day_rule === 'non_dialysis_days') days = WEEKDAYS.filter((day) => !scheduleDays.includes(day));
    else if (med.day_rule === 'specific_days') days = parseDays(med.specific_days);
    const noDays = days != null && days.length === 0;
    const from = med.start_date && med.start_date > input.today ? med.start_date : input.today;
    const label = [med.name, med.strength].filter(Boolean).join(' ');
    times.forEach((time, index) => {
      events.push({
        uid: `${med.id}-${index}@${DOMAIN}`,
        summary: `Take ${label}`,
        description: [med.dose, med.with_meals ? 'With food' : null, med.notes].filter(Boolean).join('\n') || undefined,
        date: days?.length ? firstOn(from, days) : from,
        time,
        durationMin: 15,
        rrule: `${days ? `FREQ=WEEKLY;BYDAY=${days.map((day) => BYDAY[day]).join(',')}` : 'FREQ=DAILY'}${med.end_date ? `;UNTIL=${localDateTime(med.end_date, '23:59')}` : ''}`,
        alarm: { trigger: 'PT0M', text: `Take ${label}` },
        cancelled: stopped || noDays
      });
    });
  }
  return events;
}

function vaccinationEvents(input: ReminderInput): Event[] {
  return input.vaccinations
    .filter((vaccination) => vaccination.next_due_date && vaccination.next_due_date >= input.today)
    .map((vaccination) => ({
      uid: `${vaccination.id}-0@${DOMAIN}`,
      summary: `${vaccination.vaccine_name} vaccination due`,
      description: vaccination.dose_number ? `Next dose after dose ${vaccination.dose_number}` : undefined,
      date: vaccination.next_due_date!,
      time: null,
      alarm: { trigger: 'PT9H', text: `${vaccination.vaccine_name} vaccination due today` }
    }));
}

export function reminderEvents(input: ReminderInput, options: ReminderOptions) {
  return [
    ...(options.sessions ? sessionEvents(input, options) : []),
    ...(options.medications ? medicationEvents(input) : []),
    ...(options.vaccinations ? vaccinationEvents(input) : [])
  ];
}

/** The .ics text. `now` sets DTSTAMP and SEQUENCE (minutes since 2026, so each export is newer). */
export function buildCalendar(input: ReminderInput, options: ReminderOptions, now: Date = new Date()) {
  const stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const sequence = Math.max(0, Math.floor((now.getTime() - Date.UTC(2026, 0, 1)) / 60_000));
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//RenalFlow//Reminders//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:RenalFlow',
    ...reminderEvents(input, options).flatMap((event) => eventLines(event, stamp, sequence)),
    'END:VCALENDAR'
  ];
  return `${lines.map(fold).join('\r\n')}\r\n`;
}

/**
 * Fingerprint of what reminders exported on `since` contain, to tell when that file is out of date.
 * Computed as of the export date, so days passing don't change it; first-occurrence dates are left
 * out for the same reason.
 */
export function reminderFingerprint(input: ReminderInput, options: ReminderOptions, since: string) {
  const events = reminderEvents({ ...input, today: since }, options).filter((event) => !event.cancelled);
  const text = JSON.stringify([options, events.map((event) => ({ ...event, date: event.time ? '' : event.date }))]);
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16);
}
